<template>
  <div class="container py-5">
    <div class="mb-4 d-flex align-items-center">
      <i class="bi bi-person-circle fs-1 text-primary me-3"></i>
      <h2 class="mb-0">Minha Área</h2>
    </div>

    <div class="card shadow-sm mb-4 rounded-4">
      <div class="card-body">
        <h5 class="card-title mb-3">📄 Currículo</h5>

        <div v-if="curriculo">
          <p class="mb-1">
            <span class="text-muted">Currículo enviado em:</span>
            <span class="badge px-5 py-2 bg-success ms-2">{{ curriculo.data }}</span>
          </p>
          <button
            class="btn btn-outline-primary me-2"
            @click="visualizarCurriculo"
          >
            Ver Currículo
          </button>
        </div>
        <p v-else class="text-muted">Você ainda não enviou seu currículo.</p>

        <!-- Adaptação: só libera o campo de upload se CPF estiver preenchido -->
        <div class="mt-3">
          <input
            v-if="cpfPreenchido"
            type="file"
            @change="handleUpload"
            accept=".pdf"
            class="form-control"
          />
          <span v-else class="text-danger">
            Preencha o CPF para enviar o currículo.
          </span>
        </div>
      </div>
    </div>

    <div class="card shadow-sm rounded-4">
      <div class="card-body">
        <h5 class="card-title mb-3">📌 Minhas Candidaturas</h5>

        <ul class="list-group list-group-flush" v-if="candidaturas.length">
          <li v-for="vaga in candidaturas" :key="vaga.id" class="list-group-item d-flex justify-content-between align-items-center">
            <span>
              <i class="bi bi-briefcase me-2 text-primary"></i>{{ vaga.titulo }}
            </span>
            <span class="text-muted small">{{ vaga.data }}</span>
          </li>
        </ul>
        <p v-else class="text-muted">Você ainda não se candidatou a nenhuma vaga.</p>
      </div>
    </div>
    <div class="card shadow-sm rounded-4 mt-4">
      <div class="card-body">
        <h5 class="card-title mb-3">📋 Meus Dados Pessoais</h5>

        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Telefone/Celular</label>
            <input
              v-model="dados.telefone"
              @input="dados.telefone = maskTelefone(dados.telefone)"
              type="text"
              class="form-control" :readonly="!souEu"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">CPF</label>
            <input
              v-model="dados.cpf"
              @input="dados.cpf = maskCPF(dados.cpf)"
              type="text"
              class="form-control" :readonly="!souEu"
            />
          </div>
          <div class="col-12">
            <label class="form-label">Endereço</label>
            <input v-model="dados.endereco" type="text" class="form-control" :readonly="!souEu" />
          </div>
          
          <!-- Cidade -->
          <div class="col-md-6">
            <label class="form-label">Cidade</label>
            <input v-model="dados.cidade" type="text" class="form-control" :readonly="!souEu" />
          </div>

          <!-- Estado -->
          <div class="col-md-6">
            <label class="form-label">Estado</label>
            <input v-model="dados.estado" type="text" class="form-control" :readonly="!souEu" />
          </div>
        
          <div class="col-md-6">
            <label class="form-label">Experiência Profissional</label>
            <input v-model="dados.experiencia" type="text" class="form-control" :readonly="!souEu" />
          </div>
          <div class="col-md-6">
            <label class="form-label">Raça</label>
            <select v-model="dados.raca" class="form-select" :readonly="!souEu">
              <option value="">Selecione</option>
              <option>Indígena</option>
              <option>Branca</option>
              <option>Preta</option>
              <option>Amarela</option>
              <option>Parda</option>
              <option>Prefiro não informar</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">Gênero</label>
            <select v-model="dados.genero" class="form-select" :readonly="!souEu">
              <option value="">Selecione</option>
              <option>Mulher cisgênero (Identidade de gênero correspondente a que foi atribuído no nascimento)</option>
              <option>Homem cisgênero (Identidade de gênero correspondente a que foi atribuído no nascimento)</option>
              <option>Mulher trânsgênero (Identiidade de gênero oposto ao sexo biológico)</option>
              <option>Homem trânsgênero (Identiidade de gênero oposto ao sexo biológico)</option>
              <option>Não binário</option>
              <option>Outros</option>
              <option>Prefiro não informar</option>
            </select>
          </div>
          <!-- Pessoa com deficiência -->
          <div class="col-md-6">
            <label class="form-label">Pessoa com deficiência</label>
            <select v-model="dados.deficiencia" class="form-select" :readonly="!souEu">
              <option value="">Selecione</option>
              <option>Sim</option>
              <option>Não</option>
            </select>
          </div>

          <!-- Escolaridade -->
          <div class="col-md-6">
            <label class="form-label">Escolaridade</label>
            <select v-model="dados.escolaridade" class="form-select" :readonly="!souEu">
              <option value="">Selecione</option>
              <option>Ensino fundamental incompleto ou em curso</option>
              <option>Ensino fundamental completo</option>
              <option>Ensino médio incompleto ou em curso</option>
              <option>Ensino médio completo</option>
              <option>Superior incompleto ou em curso</option>
              <option>Superior completo</option>
              <option>Pós graduação | especialização incompleto ou em curso</option>
              <option>Pós graduação | especialização completo</option>
              <option>Mestrado |doutorado | pós doutorado</option>
            </select>
          </div>

          <!-- Nome Social -->
          <div class="col-md-6">
            <label class="form-label">Nome Social</label>
            <input v-model="dados.nome_social" type="text" class="form-control" :readonly="!souEu" />
          </div>

          <!-- Naturalidade -->
          <div class="col-md-6">
            <label class="form-label">Naturalidade</label>
            <input v-model="dados.naturalidade" type="text" class="form-control" :readonly="!souEu" />
          </div>


          <!-- Está trabalhando atualmente? -->
          <div class="col-md-6">
            <label class="form-label">Está trabalhando atualmente?</label>
            <select v-model="dados.trabalhando_atualmente" class="form-select" :readonly="!souEu">
              <option value="">Selecione</option>
              <option>Sim</option>
              <option>Não</option>
            </select>
          </div>

          <!-- Data de Nascimento -->
          <div class="col-md-6">
            <label class="form-label">Data de Nascimento</label>
            <input v-model="dados.data_nascimento" type="date" class="form-control" :readonly="!souEu" />
          </div>

          <!-- Pretensão Salarial -->
      <div class="col-md-6">
  <label class="form-label">Pretensão Salarial</label>
  <div class="position-relative">
    <input
      v-model.number="dados.pretensao_salarial"
      type="range"
      class="form-range"
      min="1000"
      max="30000"
      step="100"
      :disabled="!souEu"
      style="margin-bottom: 0.5rem;"
    />

    <!-- Marcadores de faixa -->
    <div class="d-flex justify-content-between text-muted small px-1">
      <span>R$ 1.000</span>
      <span>R$ 5.000</span>
      <span>R$ 10.000</span>
      <span>R$ 20.000</span>
      <span>R$ 30.000</span>
    </div>

    <!-- Valor dinâmico exibido -->
    <div class="text-end mt-1">
      <span class="badge bg-primary fs-6">
        R$ {{ Number(dados.pretensao_salarial).toLocaleString('pt-BR') }}
      </span>
    </div>
  </div>
</div>

        </div>

        <button class="btn btn-success mt-4" @click="salvarDados">Salvar Alterações</button>
      </div>
    </div>

    <!-- Modal de aviso -->
    <div
      class="modal fade"
      tabindex="-1"
      :class="{ show: showAviso }"
      style="display: block;"
      v-if="showAviso"
      aria-modal="true"
      role="dialog"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Atenção</h5>
            <button type="button" class="btn-close" @click="showAviso = false"></button>
          </div>
          <div class="modal-body">
            <p>Mantenha seu telefone e todos os seus dados sempre atualizados, sob pena de ser desclassificado!</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click="showAviso = false">OK</button>
          </div>
        </div>
      </div>
    </div>
    <!-- Fim do modal -->
  </div>
</template>

<script>
import api from '../services/api';

export default {
  name: "UserDashboardVie",
  data() {
    return {
      curriculo: null,
      candidaturas: [],
      dados: {
        telefone: '',
        cpf: '',
        endereco: '',
        cidade: '',
        estado: '',
        escolaridade: '',
        experiencia: '',
        genero: '',
        raca: '',
        deficiencia: '',
        nome_social: '',
        naturalidade: '',
        trabalhando_atualmente: '',
        data_nascimento: '',
        pretensao_salarial: ''
      },
      usuarioId: null,
      showAviso: false
    };
  },

  computed: {
    curriculoUrl() {
      if (!this.curriculo || !this.curriculo.caminho) return '';
      const caminho = this.curriculo.caminho.startsWith('/')
        ? this.curriculo.caminho.slice(1)
        : this.curriculo.caminho;
      const backEndBase = `${window.location.protocol}//${window.location.host}/oportunidades-rh/backend/`;
      return new URL(caminho, backEndBase).href;
    },
    souEu() {
      const usuarioLogado = JSON.parse(localStorage.getItem('usuario'));
      // Supondo que o id do perfil exibido está em this.id ou this.usuario.id, e não em this.dados
      return usuarioLogado && usuarioLogado.id === this.usuarioId;
      // ou, se for this.usuario.id:
      // return usuarioLogado && usuarioLogado.id === this.usuario.id;
    },
    cpfPreenchido() {
      return this.dados.cpf && this.dados.cpf.replace(/\D/g, '').length === 11;
    }
  },

  mounted() {
    const usuario = JSON.parse(localStorage.getItem('usuario'));
    this.usuarioId = usuario.id;
    this.carregarCurriculo(usuario.id);
    this.carregarCandidaturas(usuario.id);
    this.carregarDados(usuario.id);

    // Exemplo: supondo que o tipo do usuário está em localStorage
    if (usuario && usuario.tipo === 'candidato') {
      this.showAviso = true;
    }
  },

  methods: {
    carregarCurriculo(usuarioId) {
      api.get(`buscar_curriculo.php?usuario_id=${usuarioId}`)
        .then(res => {
          const data = res.data;
          if (data && data.caminho_arquivo) {
            this.curriculo = {
              caminho: data.caminho_arquivo,
              data: new Date(data.data_upload).toLocaleDateString(),
            };
          }
        })
        .catch(error => {
          console.error('Erro ao carregar currículo:', error);
        });
    },
    carregarCandidaturas(usuarioId) {
      api.get(`minhas_candidaturas.php?usuario_id=${usuarioId}`)
        .then(res => {
          const data = res.data;
          if (Array.isArray(data)) {
            this.candidaturas = data.map(v => ({
              id: v.id,
              titulo: v.titulo,
              data: new Date(v.data_candidatura).toLocaleDateString(),
            }));
          }
        })
        .catch(error => {
          console.error('Erro ao carregar candidaturas:', error);
        });
    },
    async handleUpload(event) {
      const formData = new FormData();
      formData.append('usuario_id', this.usuarioId);
      formData.append('arquivo', event.target.files[0]);

      try {
        const res = await api.post('upload_curriculo.php', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Atualiza o objeto curriculo com o caminho retornado
        if (res.data && res.data.arquivo) {
          this.curriculo = {
            caminho: res.data.arquivo,
            // ...adicione outros campos se necessário
          };
        }
      } catch (error) {
        alert('Erro ao enviar currículo.');
      }
    },
    carregarDados(usuarioId) {
      api.get(`buscar_usuario.php?id=${usuarioId}`)
        .then(res => {
          const data = res.data;
          if (data && data.dados) {
            try {
              const dadosRecebidos = JSON.parse(data.dados);
              // Garante que todos os campos estejam presentes
              this.dados = {
                telefone: '',
                cpf: '',
                endereco: '',
                cidade: '',
                estado: '',
                escolaridade: '',
                experiencia: '',
                genero: '',
                raca: '',
                deficiencia: '',
                nome_social: '',
                naturalidade: '',
                trabalhando_atualmente: '',
                data_nascimento: '',
                pretensao_salarial: '',
                ...dadosRecebidos
              };
            } catch (e) {
              console.warn('❌ Erro ao parsear JSON de dados:', e);
            }
          }
        })
        .catch(error => {
          console.error('Erro ao carregar dados pessoais:', error);
        });
    },
    maskCPF(value) {
      return value
        .replace(/\D/g, '')           // Remove tudo que não for dígito
        .slice(0, 11)                 // Limita a 11 dígitos
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    },
    maskTelefone(value) {
      return value
        .replace(/\D/g, '')           // Remove tudo que não for dígito
        .slice(0, 11)                 // Limita a 11 dígitos
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2')
        .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
    },
    salvarDados() {
      // Remove máscara antes de enviar ao backend
      const dadosParaSalvar = {
        ...this.dados,
        cpf: this.dados.cpf.replace(/\D/g, '').slice(0, 11),
        telefone: this.dados.telefone.replace(/\D/g, '').slice(0, 11),
      };
      api.post('atualizar_dados_usuario.php', {
        id: this.usuarioId,
        dados: dadosParaSalvar,
      })
        .then(res => {
          const data = res.data;
          if (data.sucesso) {
            alert('Dados atualizados com sucesso!');
          } else {
            alert(data.erro || 'Erro ao salvar os dados.');
          }
        })
        .catch(err => {
          console.error(err);
          alert('Erro ao conectar com o servidor.');
        });
    },
    visualizarCurriculo() {
      window.open(this.curriculoUrl, '_blank');
    },
  },
};
</script>

<style scoped>
.modal {
  background: rgba(0,0,0,0.5);
}

input[type="range"]::-webkit-slider-runnable-track {
  background-color: #444; /* mais escuro */
  height: 6px;
  border-radius: 5px;
}

input[type="range"]::-moz-range-track {
  background-color: #444;
  height: 6px;
  border-radius: 5px;
}

input[type="range"]::-ms-track {
  background-color: #444;
  height: 6px;
  border-radius: 5px;
}

</style>

